import { useCallback, useState } from "react";
import { IPartnerSubscriptionChartProps } from "../../interfaces/PartnerView.interfaces";
import { CustomCardWithCharts } from "../../SharedComponents/CustomCardWithCharts";
import { ChartDataOption } from "../../SharedComponents/Interfaces/CustomCardWithCharts.interfaces";
import { groupBy } from "../../Utils/js-common";
import { PartnerSubscriptionTrendCharts } from "./PartnerSubscriptionTrendChart";

const PartnerSubscriptionChart = (props: IPartnerSubscriptionChartProps) => {
  // Properties
  const [metricFilters, setMetricFilters] = useState<{ [key: string]: any }>({});

  // Events
  const handleResetFilters = useCallback(() => {
    setMetricFilters({});
    props.onSelectedChartFilter({
      filterKey: "",
      value: ""
    });
  }, [props]);
  /**
   * onClick action for each datapoint in the chart
   */

  const handleDataPointClick = ({ field, title, value }: ChartDataOption<any>) => {
    props.onSelectedChartFilter({
      filterKey: field,
      value: value
    });
    setMetricFilters((prev) => ({ ...prev, [title]: value }));
  };

  // Events

  // const getGroupedSkuSumData = (key: string, data: any[]) => {
  //   const arr = Array.isArray(data) ? data : [];
  //   const groupedData = groupBy(arr, key);

  //   const result = Object.keys(groupedData)?.map((item) => {
  //     const skuItems = groupedData[item];
  //     const valueMap = new Map<string, number>();

  //     valueMap.set(
  //       "totalSeats",
  //       skuItems.reduce((sum: number, curr: any) => sum + (Number(curr["totalSeats"]) || 0), 0)
  //     );
  //     valueMap.set(
  //       "paidSeats",
  //       skuItems.reduce((sum: number, curr: any) => sum + (Number(curr["paidSeats"]) || 0), 0)
  //     );

  //     return {
  //       name: item,
  //       value: valueMap
  //     };
  //   });

  //   return result.sort((a, b) => (b.value.get("totalSeats") || 0) - (a.value.get("totalSeats") || 0));
  // };

  const getGroupedSumData = (key: string, data: any[], aggregationField: string) => {
    const arr = Array.isArray(data) ? data : [];
    // Find the latest snapshotDate
    const latestDate = arr.reduce((max: string, curr: any) => {
      if (!curr.snapshotDate) return max;
      return !max || new Date(curr.snapshotDate) > new Date(max) ? curr.snapshotDate : max;
    }, "");
    // Filter data for latest snapshotDate only
    const filteredData = arr.filter(
      (item) => item && item[key] && item[aggregationField] && item.snapshotDate === latestDate
    );
    const groupedData = groupBy(filteredData, key);
    return Object.keys(groupedData)?.map((item) => ({
      name: item,
      value: groupedData[item].reduce((sum: number, curr: any) => sum + (Number(curr[aggregationField]) || 0), 0)
    }));
  };

  // Data
  const tenantsBySkuFamilyData = getGroupedSumData("skuFamily", props.filteredData, "numberOfTenants");
  const totalSeatsBySkuFamilyData = getGroupedSumData("skuFamily", props.filteredData, "totalSeats");
  const paidSeatsBySkuFamilyData = getGroupedSumData("skuFamily", props.filteredData, "totalPAU");
  // const groupedSkuData = getGroupedSkuSumData("skuFamily", props.filteredData);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* <div style={{ columnGap: 10, display: "flex", margin: 8 }}>
        <CustomCardWithCharts
          loading={props.loading}
          metricFilters={metricFilters}
          onDataPointClick={handleDataPointClick}
          onResetFilters={handleResetFilters}
          options={{
            chartOptions: [
              {
                id: 1,
                field: "skuFamily",
                key: "partner-view-partner-subscription-chart-tenants-by-sku-family",
                title: "Tenants By Sku Family",
                type: "donut",
                kustoLink: props.tenantsBySkuFamilyKustoLink,
                groupedData: tenantsBySkuFamilyData
              },
              {
                id: 2,
                field: "skuFamily",
                key: "partner-view-partner-subscription-chart-paid-seats-by-sku-family",
                title: "Total/Paid Seats By Sku Family",
                type: "groupedverticalbar",
                seriesConfig: [
                  {
                    legend: "Total Seats",
                    color: getNextColor(0),
                    filterKey: "totalSeats",
                    filterValue: "skuFamily"
                  },
                  {
                    legend: "Paid Seats",
                    color: getNextColor(1),
                    filterKey: "paidSeats",
                    filterValue: "skuFamily"
                  }
                ],
                kustoLink: props.paidSeatsBySkuFamilyKustoLink,
                groupedData: groupedSkuData
              }
            ]
          }}
        />
      </div> */}
      <div style={{ columnGap: 10, display: "flex", margin: 8 }}>
        <CustomCardWithCharts
          enableUrlParams
          loading={props.loading}
          metricFilters={metricFilters}
          onDataPointClick={handleDataPointClick}
          onResetFilters={handleResetFilters}
          options={{
            chartOptions: [
              {
                id: 1,
                field: "skuFamily",
                key: "partner-view-partner-subscription-chart-tenants-by-sku-family",
                title: "Latest Month Tenants By Sku Family",
                tooltipDescription: "Distinct count of tenants associated with each Sku Family in the latest month",
                type: "donut",
                kustoLink: props.latestMonthTenantsBySkuFamilyKustoLink,
                groupedData: tenantsBySkuFamilyData
              },
              {
                id: 2,
                field: "skuFamily",
                key: "partner-view-partner-subscription-chart-total-seats-by-sku-family",
                title: "Latest Month Total Seats By Sku Family",
                tooltipDescription: "Total seats associated with each Sku Family in the latest month",
                type: "verticalbar",
                kustoLink: props.latestMonthTotalSeatsBySkuFamilyKustoLink,
                groupedData: totalSeatsBySkuFamilyData
              },
              {
                id: 3,
                field: "skuFamily",
                key: "partner-view-partner-subscription-chart-total-pau-by-sku-family",
                title: "Latest Month PAU By Sku Family",
                tooltipDescription: "Total PAU associated with each Sku Family in the latest month",
                type: "verticalbar",
                kustoLink: props.latestMonthTotalPAUBySkuFamilyKustoLink,
                groupedData: paidSeatsBySkuFamilyData
              }
            ]
          }}
        />
      </div>
      <div style={{ columnGap: 10, display: "flex", margin: 8 }}>
        <PartnerSubscriptionTrendCharts
          loading={props.loading}
          metricFilters={metricFilters}
          onDataPointClick={handleDataPointClick}
          onResetFilters={handleResetFilters}
          filteredData={props.filteredData}
          tenantsTrendBySkuFamilyKustoLink={props.tenantsTrendBySkuFamilyKustoLink}
          totalSeatsTrendBySkuFamilyKustoLink={props.totalSeatsTrendBySkuFamilyKustoLink}
          totalPAUTrendBySkuFamilyKustoLink={props.totalPAUTrendBySkuFamilyKustoLink}
        />
      </div>
    </div>
  );
};

export default PartnerSubscriptionChart;
